import { ActionConfirmation } from "../ui/ActionConfirmation.js";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { requestPasswordReset } from "../api.js";
import { Brand } from "../Brand.js";

/**
 * Asking for a reset link.
 *
 * The confirmation deliberately does not say whether an account was found. The
 * server answers the same way either way, and a page that said "no account
 * with that address" would hand back exactly what the server refuses to.
 */
export function ForgotPassword() {
  const [identifier, setIdentifier] = useState("");
  const [asked, setAsked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(identifier);
      setAsked(true);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Could not send a reset link.");
    } finally {
      setBusy(false);
    }
  }

  if (asked) {
    return (
      <div className="signin">
        <div style={{ width: 320 }}>
          <Brand title="Wirral Music Factory" subtitle="Hub" className="brand-auth" />
          <ActionConfirmation variant="success" title="Password reset requested" description={<>
            If there is an account for that, a link is on its way. It works once and lasts an hour.
           </>} />
          <p className="small muted">
            A child&rsquo;s reset goes to their parent&rsquo;s address rather than to them. If
            nothing arrives, check the spam folder — and that it is the address the school holds.
          </p>
          <Link className="small" to="/">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="signin">
      <form onSubmit={submit}>
        <Brand title="Wirral Music Factory" subtitle="Hub" className="brand-auth" />

        {error && <div className="error small">{error}</div>}

        <label className="small muted" htmlFor="identifier">
          Email or username
        </label>
        {/* type="text" for the same reason as on the sign-in form: a child
            signs in with a username, which no browser will accept as an email. */}
        <input
          id="identifier"
          type="text"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          required
        />

        <button type="submit" disabled={busy}>
          {busy ? "Sending…" : "Send a reset link"}
        </button>

        <Link className="small muted" to="/">
          Back to sign in
        </Link>
      </form>
    </div>
  );
}
